import {
  Box,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import {
  IndexFredCode,
  UpdateFrequencyName
} from 'generated/graphql';
import _ from 'lodash';

import { StyledTextChip } from 'modules/components/layer01';
import { useMemo } from 'react';



export type StandardSourceMetricCardProps = {
  name: string;
  value: Big;
  displayUnit: string;
  changeDisplayUnit: string;
  upIsGood: boolean;
  changeMoM: Big | null;
  color?: 'PRIMARY' | 'SECONDARY';
  fredCode: IndexFredCode;
  fredUrl: string;
  updateFrequency: UpdateFrequencyName;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: StandardSourceMetricCardProps) => {
  const theme = useTheme();

  const changeIsAGoodChange = useMemo(() => {
    return (props.upIsGood && props.changeMoM?.gte(0))
      || (!props.upIsGood && props.changeMoM?.lt(0));
  }, [
    props.changeMoM,
    props.upIsGood
  ]);

  const valueColor = useMemo(() => {
    if (changeIsAGoodChange) {
      return props.color === 'PRIMARY' ? theme.palette.primary.main : theme.palette.primary.darker200;
    } else {
      return theme.palette.error.main;
    }
  }, [
    changeIsAGoodChange,
    props.color,
    theme.palette.error.main,
    theme.palette.primary.darker200,
    theme.palette.primary.main
  ]);

  const textColor = useMemo(() => {
    if (props.color === 'PRIMARY') {
      return '';
    }

    return theme.palette.gray.light80;
  }, [
    props.color,
    theme.palette.gray.light80
  ]);



  return (
    <Box
      key={props.fredCode}
      width='300px'
      padding='.5rem .75rem .75rem .75rem'
      display='flex'
      flexDirection='column'
      justifyContent='space-between'
      borderRadius='.5rem'
      rowGap='.5rem'
      sx={{
        cursor: 'pointer'
      }}
      onClick={() => {
        window.open(props.fredUrl, '_blank');
      }}
    >
      <Typography
        overflow='hidden'
        textOverflow='ellipsis'
        noWrap={true}
        variant='summaryMetricName'
        color={textColor}
      >
        {props.name}
      </Typography>

      <Stack
        direction='row'
        alignItems='start'
      >
        <Typography
          variant='summaryMetricValue'
          color={valueColor}
        >
          {__formatNumber(props.value)}
        </Typography>

        <Typography
          variant='h4'
          fontWeight={900}
          color={valueColor}
        >
          {props.displayUnit}
        </Typography>
      </Stack>

      <Box>
        <StyledTextChip.FC
          upIsGood={props.upIsGood}
          label={__getChipRelativeChangeText(props.changeMoM, props.changeDisplayUnit)}
          type={__getChipType(props.changeMoM)}
        />
      </Box>
    </Box>
  );
};



function __getChipType(change: Big | null) {
  if (change === null) {
    return 'PRIMARY';
  }

  if (change.gte(0)) {
    return 'STAT_INCREASE';
  }

  return 'STAT_DECREASE';
}



function __formatNumber(val: Big) {
  return val.toNumber()
    .toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
}



function __getChipRelativeChangeText(changeValue: Big | null, unit: string, numFractionalDigits = 1) {
  if (_.isNil(changeValue)) {
    return '-';
  }

  const val = changeValue
    .toNumber()
    .toLocaleString('en-us', {
      minimumFractionDigits: numFractionalDigits,
      maximumFractionDigits: numFractionalDigits
    });
  return `${val} ${unit}`;
}
